import { useEffect, useState } from 'react';
import { useChatbot } from '../../contexts/ChatbotContext';
import { useChatAPI } from '../../hooks/useChatAPI';
import { useChatMessages } from '../../hooks/useChatMessages';
import { useConversationContext } from '../../hooks/useConversationContext';
import { useConversationTimer } from '../../hooks/useConversationTimer';
import { useDesignColors, useLoadColorsOnChatbotChange } from '../../hooks/useDesignColors';
import { useMultilingualSettings } from '../../hooks/useMultilingualSettings';
import { useStreamingResponse } from '../../hooks/useStreamingResponse';
import { useThemeSettings } from '../../hooks/useThemeSettings';
import { useCustomTexts } from '../../hooks/useCustomTexts';
import { useMiniApps } from '../../hooks/useMiniApps';
import { useLogos } from '../../hooks/useLogos';
import { useWidgetSettings } from '../../hooks/useWidgetSettings';
import { generateUUID } from '../../utils/uuid';
import { ChatHeader } from './ChatHeader';
import { ChatInput } from './ChatInput';
import { ChatMessages } from './ChatMessages';
import { PoweredBy } from './PoweredBy';
import { NoChatbotFound } from './NoChatbotFound';
import { useMiniAppModal } from '../../hooks/useMiniAppModal';
import { MiniAppId } from '../../utils/miniAppsIcons';
import { supabase } from '../../lib/supabase';

interface ChatInterfaceProps {
  preview?: boolean;
  isWidget?: boolean;
  publicId?: string;
}

export function ChatInterface({ preview = false, isWidget = false, publicId }: ChatInterfaceProps) {
  const { colors, initializeColors } = useDesignColors();
  const { theme, initializeTheme } = useThemeSettings();
  const { messages, addMessage, setChatbotPublicId } = useChatMessages();
  const { sendMessage } = useChatAPI();
  const { streamResponse, isStreaming } = useStreamingResponse();
  const { addToContext } = useConversationContext();
  const { settings: multilingualSettings, initialized: multilingualInitialized, initializeSettings: initializeMultilingualSettings } = useMultilingualSettings();
  const { welcomeMessage, initializeTexts, initialized: textsInitialized } = useCustomTexts();
  const { chatbotPublicId: contextChatbotId, isLoading } = useChatbot();
  const { initializeMiniApps, miniApps } = useMiniApps();
  const { openModal, setActiveApp } = useMiniAppModal();
  const { initializeLogos } = useLogos();
  const { initializeSettings } = useWidgetSettings();
  const isDark = theme.mode === 'dark';
  const [isInitialized, setIsInitialized] = useState(false);
  const [initError, setInitError] = useState<Error | null>(null);

  // Determine which chatbotId to use - prioritize prop over context
  const chatbotId = publicId || contextChatbotId;

  // Load colors when chatbot changes
  useLoadColorsOnChatbotChange();

  // Set the chatbotPublicId in the chat messages store
  useEffect(() => {
    if (chatbotId) {
      setChatbotPublicId(chatbotId);
    }
  }, [chatbotId, setChatbotPublicId]);

  // Initialize all settings
  useEffect(() => {
    const initializeWidget = async () => {
      if (!chatbotId) {
        setInitError(new Error("ID du chatbot manquant"));
        setIsInitialized(false);
        return;
      }

      try {
        // Initialize all configurations in parallel
        await Promise.all([
          initializeColors(chatbotId),
          initializeTexts(chatbotId),
          initializeMiniApps(chatbotId),
          initializeTheme(chatbotId),
          initializeMultilingualSettings(chatbotId),
          initializeSettings(chatbotId),
        ]).catch((err) => {
          console.warn("Error initializing some configurations:", err);
        });

        // Get config for logos
        const { data: config } = await supabase
          .from("chatbot_configs")
          .select("logos")
          .eq("chatbot_public_id", chatbotId)
          .single();

        if (config?.logos && typeof config.logos === "object") {
          initializeLogos(
            config.logos as {
              chatbotLogo: string | null;
              botIcon: string | null;
            }
          );
        }

        setIsInitialized(true);
        setInitError(null);
      } catch (err) {
        console.error("Error initializing widget:", err);
        setInitError(
          err instanceof Error
            ? err
            : new Error("Une erreur est survenue lors de l'initialisation")
        );
        setIsInitialized(false);
      }
    };

    initializeWidget();
  }, [chatbotId]);

  // Send welcome message when texts are initialized
  useEffect(() => {
    if (textsInitialized && messages.length === 0) {
      addMessage({
        type: 'bot',
        content: welcomeMessage
      });
    }
  }, [textsInitialized, welcomeMessage, messages.length, addMessage]);

  // Always call hooks unconditionally, but pass the preview flag to control behavior
  useConversationTimer(messages, isStreaming, preview);

  // Gestionnaire pour l'événement open-mini-app
  const handleOpenMiniApp = (event: CustomEvent) => {
    const { id, filters } = event.detail || {};
    if (id) {
      openModal(id, filters);
    }
  };

  // Communication avec le parent pour les mini-apps et le widget
  useEffect(() => {
    // 1. Écouter les messages du parent (widget.js)
    const handleParentMessage = (event: MessageEvent) => {
      if (event.data && event.data.type === 'open-mini-app') {
        const { appId, filters } = event.data;
        if (appId && setActiveApp) {
          setActiveApp(appId as MiniAppId, filters);
        }
      }
      
      if (event.data && event.data.type === 'open-products-miniapp') {
        const { productIds } = event.data;
        if (productIds && productIds.length > 0 && setActiveApp) {
          const filters = {
            products: {
              mentionedIds: productIds
            }
          };
          setActiveApp('products', filters);
        }
      }
    };

    // Ajouter l'écouteur seulement si en mode widget (dans un iframe)
    if (isWidget && typeof window !== 'undefined' && window.parent !== window) {
      window.addEventListener('message', handleParentMessage);
    }

    // Ajouter l'écouteur d'événements DOM standard
    document.addEventListener('open-mini-app', handleOpenMiniApp as EventListener);

    // Nettoyage
    return () => {
      if (isWidget && typeof window !== 'undefined') {
        window.removeEventListener('message', handleParentMessage);
      }
      document.removeEventListener('open-mini-app', handleOpenMiniApp as EventListener);
    };
  }, [openModal, setActiveApp, isWidget]);

  // Méthode pour envoyer un message au parent (widget.js)
  const sendMessageToParent = (message: any) => {
    if (isWidget && typeof window !== 'undefined' && window.parent !== window) {
      window.parent.postMessage(message, '*');
    }
  };

  // Méthode pour ouvrir une mini-app et envoyer l'événement au parent
  const openMiniApp = (appId: MiniAppId, filters?: any) => {
    // Ouvrir localement
    if (setActiveApp) {
      setActiveApp(appId, filters);
    }
    
    // Envoyer au parent
    sendMessageToParent({
      type: 'open-mini-app',
      appId,
      filters
    });
  };

  const handleSendMessage = async (content: string) => {
    const userMessage = {
      type: 'user' as const,
      content,
    };

    addMessage(userMessage);
    
    // Add the message to the context with the complete Message object
    const messageWithId = {
      ...userMessage,
      id: generateUUID(),
      timestamp: new Date().toISOString()
    };
    addToContext(messageWithId);

    try {
      // The context is retrieved automatically in the useChatAPI hook
      const stream = await sendMessage(content);
      
      // Process the stream response
      const botResponse = await streamResponse(stream);
      
      // Since we can't pass the callback directly, manually create a bot message for context
      addToContext({
        id: generateUUID(),
        type: 'bot',
        content: botResponse,
        timestamp: new Date().toISOString()
      });

      // Vérifier les commandes spéciales pour les mini-apps
      checkForMiniAppCommands(content, botResponse);
    } catch (err) {
      console.error('Error:', err);
      // Utiliser le message d'erreur personnalisé des design settings au lieu d'un message codé en dur
      const { errorMessage } = useCustomTexts.getState();
      addMessage({
        type: 'bot',
        content: errorMessage
      });
    }
  };

  // Fonction pour détecter les commandes d'ouverture de mini-apps
  const checkForMiniAppCommands = (userMessage: string, botResponse: string) => {
    // Vérifier les demandes de produits
    const productRegex = /(?:voir|montrer|afficher|trouver) les? produits?/i;
    if (productRegex.test(userMessage) || productRegex.test(botResponse)) {
      // Si les mini-apps sont activées, ouvrir l'app produits
      if (miniApps.some(app => app.id === 'products' && app.enabled)) {
        setTimeout(() => openMiniApp('products'), 500);
      }
      return;
    }

    // Vérifier les demandes de calendrier/rendez-vous
    const calendarRegex = /(?:rendez-vous|calendrier|prendre un rdv|réserver)/i;
    if (calendarRegex.test(userMessage) || calendarRegex.test(botResponse)) {
      if (miniApps.some(app => app.id === 'calendar' && app.enabled)) {
        setTimeout(() => openMiniApp('calendar'), 500);
      }
      return;
    }

    // Vérifier les demandes de services
    const servicesRegex = /(?:voir|montrer|afficher) les? services/i;
    if (servicesRegex.test(userMessage) || servicesRegex.test(botResponse)) {
      if (miniApps.some(app => app.id === 'services' && app.enabled)) {
        setTimeout(() => openMiniApp('services'), 500);
      }
      return;
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Show no chatbot found only if we're sure there's no chatbot ID
  if (!chatbotId) {
    return <NoChatbotFound isWidget={isWidget} />;
  }

  return (
    <div className="relative flex flex-col h-full">
      <div 
        className="absolute inset-0 rounded-xl"
        style={{
          background: `linear-gradient(to right, ${colors.gradientStart}, ${colors.gradientEnd})`,
        }}
      >
        <div 
          className={`absolute inset-[1px] rounded-xl overflow-hidden flex flex-col ${
            isDark ? 'bg-gray-900' : 'bg-gray-100'
          }`}
        >
          <div className={`${isDark ? 'bg-gray-800/95' : 'bg-white'} border-b ${
            isDark ? 'border-gray-700' : 'border-gray-200'
          }`}>
            <ChatHeader />
          </div>
          
          <div className={`flex-1 overflow-hidden relative ${
            isDark ? 'bg-gray-900' : 'bg-[#EBEDF0]'
          }`}>
            <ChatMessages />
          </div>
          
          <div className={`flex flex-col border-t shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] ${
            isDark 
              ? 'border-gray-700 bg-gray-800/95' 
              : 'border-gray-200 bg-gray-200'
          }`}>
            <div className="p-4">
              {!isWidget && <PoweredBy />}
              <ChatInput onSendMessage={handleSendMessage} disabled={isStreaming} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}